import { cn } from '@/utils/cn'

export interface ButtonProps {
  children: string | HTMLElement
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  onClick?: () => void
  href?: string
  type?: 'button' | 'submit'
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  className,
  onClick,
  href,
  type = 'button',
}: ButtonProps): HTMLElement {
  const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95'

  const variants = {
    primary: 'btn-primary', // Uses the class defined in index.css
    secondary: 'btn-secondary', // Uses the class defined in index.css
    outline: 'border-2 border-slate-200 text-slate-600 hover:border-orange-500 hover:text-orange-600 bg-transparent',
    ghost: 'text-slate-600 hover:text-orange-600 hover:bg-orange-50',
  }

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  }

  const classes = cn(baseStyles, variants[variant], sizes[size], className)

  if (href) {
    const link = document.createElement('a')
    link.href = href
    link.className = classes
    if (typeof children === 'string') {
      link.textContent = children
    } else {
      link.appendChild(children)
    }
    link.addEventListener('click', (e) => {
      e.preventDefault()
      if (onClick) onClick()
      // Use router navigation instead of direct href
      window.dispatchEvent(new CustomEvent('navigate', { detail: href }))
    })
    return link
  }

  const button = document.createElement('button')
  button.type = type
  button.className = classes
  if (typeof children === 'string') {
    button.textContent = children
  } else {
    button.appendChild(children)
  }
  if (onClick) {
    button.addEventListener('click', onClick)
  }

  return button
}
